/**
 * Mapeia o status da empresa (numérico ou textual) em um modelo interno
 * usado pela UI de Acompanhamento, incluindo:
 *   - rótulo exibível,
 *   - etapa atual e total de etapas (com e sem SUPEX),
 *   - flag de rejeitado / finalizado.
 *
 * Espelha o statusMapper do CadastroEmpresa, mas adaptado ao contrato
 * dos endpoints /tca e /tca-empregador da api-autorizacao-estagio-aprendiz.
 */

export const STATUS_LABELS = {
  PENDENTE: "Pendente",
  AGUARDANDO_ESTUDANTE: "Aguardando preenchimento do Estudante",
  AGUARDANDO_VALIDACAO_CIEEMG: "Aguardando validação do CIEEMG",
  EM_VALIDACAO: "Em validação pelo CIEEMG",
  AGUARDANDO_CONFECCAO: "Aguardando confecção do contrato",
  CONTRATO_CONFECCIONADO: "Contrato confeccionado",
  AGUARDANDO_ASSINATURAS: "Aguardando assinaturas",
  DISPONIBILIZADO_PARA_ASSINATURA: "Disponibilizado para assinatura",
  FINALIZADO: "Finalizado",
  APROVADO: "Aprovado",
  REPROVADO_EMPRESA: "Reprovado — voltar p/ Empresa",
  REPROVADO_ESTUDANTE: "Reprovado — voltar p/ Estudante",
  REPROVADO_DEFINITIVO: "Reprovado definitivamente",
  RECUSADO: "Recusado",
};

/**
 * Tipos de programa que exigem etapa extra "Preenchimento do Estudante"
 * entre "Cadastro inicial" e "Validação do preenchimento".
 * Espelha o `PROGRAMA_REQUER_ESTUDANTE` de utils/tipoPrograma.js.
 * Duplicado aqui para evitar import circular (mappers não dependem de utils).
 */
const PROGRAMA_REQUER_ESTUDANTE = new Set([0, 2]);

// Mapeia o status retornado pelo backend para a etapa equivalente no stepper
// quando o programa EXIGE a etapa extra do estudante (programa 0 ou 2).
//   1) Cadastro inicial
//   2) Preenchimento do Estudante
//   3) Validação do preenchimento
//   4) Aguardando assinatura
//   5) Contrato finalizado
export const STATUS_PARA_ETAPA_COM_ESTUDANTE = {
  AGUARDANDO_ESTUDANTE: 2,
  AGUARDANDO_VALIDACAO_CIEEMG: 3,
  PENDENTE: 3,
  EM_VALIDACAO: 3,
  AGUARDANDO_CONFECCAO: 3,
  CONTRATO_CONFECCIONADO: 3,
  AGUARDANDO_ASSINATURAS: 4,
  DISPONIBILIZADO_PARA_ASSINATURA: 4,
  FINALIZADO: 5,
  REPROVADO_EMPRESA: 1,
  REPROVADO_ESTUDANTE: 2,
  REPROVADO_DEFINITIVO: 1,
  APROVADO: 5,
  RECUSADO: 1,
};

// Mapeia o status retornado pelo backend para a etapa equivalente no stepper
// quando o programa NÃO exige etapa extra do estudante (programa 1).
//   1) Cadastro inicial
//   2) Validação do preenchimento
//   3) Aguardando assinatura
//   4) Contrato finalizado
// Mantido como mapa separado para refletir fielmente o fluxo do TCA padrão,
// onde o cadastro da empresa já contempla o preenchimento do estudante.
export const STATUS_PARA_ETAPA_SEM_ESTUDANTE = {
  AGUARDANDO_ESTUDANTE: 1,
  AGUARDANDO_VALIDACAO_CIEEMG: 2,
  PENDENTE: 2,
  EM_VALIDACAO: 2,
  AGUARDANDO_CONFECCAO: 2,
  CONTRATO_CONFECCIONADO: 2,
  AGUARDANDO_ASSINATURAS: 3,
  DISPONIBILIZADO_PARA_ASSINATURA: 3,
  FINALIZADO: 4,
  REPROVADO_EMPRESA: 1,
  REPROVADO_ESTUDANTE: 1,
  REPROVADO_DEFINITIVO: 1,
  APROVADO: 4,
  RECUSADO: 1,
};

// Alias para compatibilidade com consumidores que ainda esperam o nome
// "STATUS_PARA_ETAPA" (sem sufixo). Aponta para o mapa COM estudante, que é
// o que o Acompanhamento sempre utilizou (5 etapas). Quando a regra de
// "Preenchimento do Estudante" por programa for religada na view, basta
// passar `tipoPrograma` para `mapearEtapaPorStatus` e ela escolherá entre
// STATUS_PARA_ETAPA_COM_ESTUDANTE e STATUS_PARA_ETAPA_SEM_ESTUDANTE.
export const STATUS_PARA_ETAPA = STATUS_PARA_ETAPA_COM_ESTUDANTE;

export const STATUS_CORES = {
  PENDENTE: "#3b82f6",
  AGUARDANDO_ESTUDANTE: "#3b82f6",
  AGUARDANDO_VALIDACAO_CIEEMG: "#3b82f6",
  EM_VALIDACAO: "#1e40af",
  AGUARDANDO_CONFECCAO: "#3b82f6",
  CONTRATO_CONFECCIONADO: "#1e40af",
  AGUARDANDO_ASSINATURAS: "#3b82f6",
  DISPONIBILIZADO_PARA_ASSINATURA: "#1e40af",
  FINALIZADO: "#134b8b",
  APROVADO: "#1e3a8a",
  REPROVADO_EMPRESA: "#ef4444",
  REPROVADO_ESTUDANTE: "#ef4444",
  REPROVADO_DEFINITIVO: "#dc2626",
  RECUSADO: "#dc2626",
};

export const ETAPA_FINALIZADO_PADRAO = 5;

/** Total de etapas quando o programa EXIGE etapa extra do estudante (0 ou 2).
 *  Fluxo: Cadastro inicial → Preenchimento do Estudante →
 *         Validação → Aguardando assinatura → Contrato finalizado. */
export const TOTAL_ETAPAS_COM_ESTUDANTE = 5;

/** Total de etapas quando o programa NÃO exige etapa extra do estudante (1).
 *  Fluxo: Cadastro inicial → Validação → Aguardando assinatura →
 *         Contrato finalizado. */
export const TOTAL_ETAPAS_PADRAO = 4;

/** @deprecated Mantido por compatibilidade legada. Use
 *  TOTAL_ETAPAS_COM_ESTUDANTE ou TOTAL_ETAPAS_PADRAO conforme o programa. */
export const TOTAL_ETAPAS_LEGADO_5 = 5;

function normalizarStatusKey(valor) {
  return String(valor || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
}

export function obterStatusChave(status) {
  return normalizarStatusKey(status);
}

export function getStatusLabel(status) {
  const chave = obterStatusChave(status);
  if (!chave) return "Sem status";
  return STATUS_LABELS[chave] || chave;
}

export function getStatusCor(status) {
  const chave = obterStatusChave(status);
  return STATUS_CORES[chave] || "#94a3b8";
}

/**
 * Retorna o total de etapas para o stepper de acordo com o `tipoPrograma`.
 */
export function obterTotalEtapasPorPrograma(tipoPrograma, requerSupex = false) {
  const base = PROGRAMA_REQUER_ESTUDANTE.has(Number(tipoPrograma))
    ? TOTAL_ETAPAS_COM_ESTUDANTE
    : TOTAL_ETAPAS_PADRAO;
  return requerSupex ? base + 1 : base;
}

/**
 * Converte um status textual em etapa do stepper, considerando:
 *   - `tipoPrograma` (0 ou 2 → usa mapa com etapa extra do estudante);
 *   - `requerSupex` (insere etapa SUPEX antes do "finalizado").
 */
export function mapearEtapaPorStatus(status, requerSupex = false, tipoPrograma = 1) {
  const chave = obterStatusChave(status);
  if (!chave) return 1;

  const mapa = PROGRAMA_REQUER_ESTUDANTE.has(Number(tipoPrograma))
    ? STATUS_PARA_ETAPA_COM_ESTUDANTE
    : STATUS_PARA_ETAPA_SEM_ESTUDANTE;
  const etapa = mapa[chave];
  if (!Number.isFinite(etapa)) return 1;

  const totalBase = obterTotalEtapasPorPrograma(tipoPrograma, false);

  // Quando há SUPEX (etapa extra), inserimos a etapa SUPEX antes da etapa
  // de assinatura, totalizando (base + 1) etapas visíveis.
  if (requerSupex) {
    const limite = PROGRAMA_REQUER_ESTUDANTE.has(Number(tipoPrograma)) ? 3 : 2;
    if (etapa >= limite) {
      return Math.min(etapa + 1, totalBase + 1);
    }
  }

  // Garante que o número da etapa nunca ultrapasse o total de etapas
  // exibidas pelo stepper (evita "Etapa 6/5").
  return Math.min(etapa, totalBase);
}

/**
 * Determina a etapa corrente do stepper considerando o status recebido.
 */
export function resolverEtapa(status, requerSupex = false, tipoPrograma = 1) {
  return mapearEtapaPorStatus(status, requerSupex, tipoPrograma);
}

export function ehRejeitado(status) {
  const chave = obterStatusChave(status);
  return (
    chave === "REPROVADO_EMPRESA" ||
    chave === "REPROVADO_ESTUDANTE" ||
    chave === "REPROVADO_DEFINITIVO" ||
    chave === "RECUSADO"
  );
}

export function ehFinalizado(status) {
  const chave = obterStatusChave(status);
  return chave === "FINALIZADO" || chave === "APROVADO";
}

/**
 * Modelo agregado que a UI consome: rótulo, cor, etapa, flags e total.
 * Aceita:
 *   - `etapaRejeicaoHint` opcional (1..totalEtapas) para sobrescrever a
 *     etapa quando o item é REPROVADO_* e a etapa real foi inferida por
 *     um hint externo (ex.: comentário do andamentos fake);
 *   - `tipoPrograma` (0, 1 ou 2) para ajustar o total de etapas e o
 *     mapeamento do status à etapa correta do stepper.
 */
export function normalizarStatusCadastro(
  empresaOuItem,
  payload,
  requerSupex = false,
  etapaRejeicaoHint = null,
  tipoPrograma = 1,
) {
  const chave = obterStatusChave(
    empresaOuItem?.status ??
      payload?.status ??
      empresaOuItem?.statusCadastro ??
      payload?.statusCadastro,
  );

  if (!chave) return null;

  const totalBase = obterTotalEtapasPorPrograma(tipoPrograma, false);
  const totalEtapas = requerSupex ? totalBase + 1 : totalBase;

  let etapaAtual = mapearEtapaPorStatus(chave, requerSupex, tipoPrograma);

  // Se houver hint de rejeição e o status for de reprovação, sobrescreve
  // a etapa com o valor do hint (limitado ao total de etapas).
  if (
    etapaRejeicaoHint &&
    ehRejeitado(chave) &&
    Number.isFinite(Number(etapaRejeicaoHint))
  ) {
    etapaAtual = Math.min(
      Math.max(1, Number(etapaRejeicaoHint)),
      totalEtapas,
    );
  }

  return {
    chaveInterna: chave,
    statusLabel: getStatusLabel(chave),
    statusCor: getStatusCor(chave),
    etapaAtual,
    rejeitado: ehRejeitado(chave),
    finalizado: ehFinalizado(chave),
    totalEtapas,
    requerSupex,
    tipoPrograma: Number(tipoPrograma) || 1,
  };
}
